import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import * as IssuesV2Store from '../../../../redux/issuesV2';
import type { Status } from '../../../../../shared/issuesV2';

import useFetchIssuesAction from './useFetchIssuesAction';

/**
 * Hook which powers the IssueStatusTabs.
 * Grabs the status summary as well as AsyncStatus for it,
 * and also provides the methods for updating the current issue status.
 *
 * Note that this is NOT responsible for re-fetching the issue status summary,
 * as that is coupled with re-fetching issues in the `useFetchIssuesAction`.
 */
const useStatusSummary = () => {
  const dispatchSetStatus = useFetchIssuesAction(IssuesV2Store.actions.setStatus);
  //  The active status
  const status = useSelector(IssuesV2Store.selectors.getStatus);
  //  Method for updating the status
  const setStatus = useCallback((nextStatus: Status) => {
    //  Only actually dispatch the action if we are changing the status, ignore if they clicked the current active one.
    if (nextStatus !== status) {
      dispatchSetStatus(nextStatus);
    }
  }, [dispatchSetStatus, status]);

  const grouping = useSelector(IssuesV2Store.selectors.getGrouping);

  const { loading: isLoading } = useSelector(IssuesV2Store.selectors.getAsyncStatusStatusSummary);
  const statusSummary = useSelector(IssuesV2Store.selectors.getStatusSummary);

  return {
    status,
    setStatus,
    grouping,
    isLoading,
    statusSummary,
  };
};

export default useStatusSummary;
